<template>
  <div
    class="tm-alert"
    :class="{
      'tm-alert--small': size === 'small',
      'tm-alert--medium': size === 'medium',
      'tm-alert--xSmall': size === 'xSmall',
      'tm-alert--bordered': bordered,
      'tm-alert--no-border-radius': noBorderRadius,
      'tm-alert--reduced': isReduced,
      'tm-alert--justify-center': justifyCenter,
    }"
  >
    <div
      class="tm-alert__wrapper"
      :class="{ 'tm-alert__wrapper--align-center': alignCenter }"
    >
      <slot name="icon">
        <tm-icon
          v-if="icon"
          class="tm-alert__icon"
          :name="icon"
          :size="size === 'xSmall' ? 'medium' : 'xLarge'"
        />
      </slot>

      <div class="tm-alert__content">
        <div class="tm-alert__content__container">
          <div
            v-if="title"
            class="subhead-semi-bold-16 emphsize--text mb-1"
          >
            {{ title }}
          </div>

          <div
            class="tm-alert__body"
            :class="{
              'tm-alert__body--reduced': isReduced,
            }"
          >
            <slot />
          </div>
        </div>

        <div
          v-if="hasAlertButtonSlot || showCloseBtn"
          class="tm-alert__content__button"
          :class="{
            'tm-alert__content__button--top': alignButtonTop,
            'tm-alert__content__button--close': showCloseBtn && !hasAlertButtonSlot,
          }"
        >
          <slot name="alert-button">
            <tm-button
              v-if="showCloseBtn"
              size="small"
              class="tm-alert__close-btn"
              flat
              icon-only
              color="transparent"
              @click="$emit('close')"
            >
              <tm-icon
                name="close"
                size="small"
                class="neutral--text"
              />
            </tm-button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from '@/composition/vue/compositionApi'
import type { SizeProp } from '@/components/shared/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmButton from '@/components/shared/TmButton.vue'
import type { IconName } from '@/assets/icons/icons'

export default defineComponent({
  components: {
    TmButton,
    TmIcon,
  },
  props: {
    icon: {
      type: String as PropType<IconName>,
    },
    title: {
      type: String,
    },
    noBorderRadius: {
      type: Boolean,
    },
    alignButtonTop: {
      type: Boolean,
    },
    alignCenter: {
      type: Boolean,
      default: false,
    },
    justifyCenter: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as SizeProp<'' | 'small' | 'medium' | 'xSmall'>,
      default: '',
    },
    showCloseBtn: {
      type: Boolean,
    },
    bordered: {
      type: Boolean,
    },
    isReduced: {
      type: Boolean,
    },
  },
  emits: {
    close: () => true,
  },
  setup(_props, context) {
    const hasAlertButtonSlot = context.slots['alert-button']
    return {
      hasAlertButtonSlot,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins';

.tm-alert {
  display: flex;
  align-items: center;
  border-radius: $border-radius-md;
  padding: 20px;
  border: none;
  min-height: 48px;

  &__close-btn {
    &:hover::before {
      background: $gray9 !important;
      opacity: 0.1 !important;
    }
  }

  &--small {
    min-height: 36px;
    padding: 8px 10px;
    border-radius: $border-radius;

    .tm-alert__wrapper {
      gap: 10px;
    }
  }

  &--xSmall {
    min-height: 36px;
    padding: 8px 10px;
    border-radius: $border-radius;

    .tm-alert__wrapper {
      gap: 10px;
    }
    .tm-alert__body {
      font-size: $sm-font;
      line-height: 16px;
    }
    .tm-alert__icon {
      margin-top: 0;
    }
  }

  &--medium {
    min-height: 48px;
    padding: 12px 16px;
  }

  &--reduced {
    padding: 12px;
  }

  &--no-border-radius {
    border-radius: 0;
  }

  &__wrapper {
    display: flex;
    flex: 1;
    align-items: flex-start;
    gap: 12px;
    max-width: 100%;

    &--align-center {
      align-items: center;

      .tm-alert__content {
        align-items: center;
        &__button {
          align-self: unset;
        }
      }

      .tm-alert__content__container {
        align-self: center;
      }
    }
  }

  &__content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    align-self: center;
    gap: 32px;
    max-width: 100%;
    @include overflow-hidden-with-clip-margin;

    @include media-sm-x-max {
      gap: 16px;
    }

    &__button {
      display: flex;

      &--top {
        align-self: flex-start;
      }

      &--close {
        align-self: flex-start;
        align-items: center;
        height: 20px;
      }
    }

    &__container {
      flex: 1;
      max-width: 100%;
      align-self: stretch;
    }
  }

  &__body {
    font-size: $font-size-root;
    font-weight: $regular-font;
    line-height: 20px;
    color: $emphasize;
    &--reduced {
      font-size: $sm-x-font;
    }
  }

  &__icon {
    margin-top: -1px;
  }

  &.warning {
    background: $orange100 !important;
    .tm-alert__icon {
      color: $orange !important;
    }
  }
  &.error {
    background: $red100 !important;
    .tm-alert__icon {
      color: $red !important;
    }
  }
  &.success {
    background: $green100 !important;
    .tm-alert__icon {
      color: $green !important;
    }
  }
  &.info {
    background: $blue100 !important;
    .tm-alert__icon {
      color: $action !important;
    }
  }
  &.promotion {
    background: linear-gradient(90.71deg, rgba(170, 65, 207, 0.08) 7.36%, rgba(77, 97, 201, 0.08) 85.2%) !important;
    .tm-alert__icon {
      background: linear-gradient(90.71deg, #aa41cf 7.36%, #4d61c9 85.2%) !important;
      -webkit-background-clip: text !important;
      -webkit-text-fill-color: transparent !important;
    }
  }
  &.hint {
    background: $shade !important;
    .tm-alert__icon {
      color: $action !important;
    }
    .tm-alert__body {
      color: $gray7 !important;
    }
  }

  :deep(.tm-btn) {
    &:hover:before {
      background-color: $transparent !important;
    }

    & i {
      &:hover {
        color: $black !important;
      }
    }
  }

  &--bordered {
    border: 1px solid $border-color !important;
    box-shadow: $white-block-shadow;
  }

  &--justify-center {
    justify-content: center;

    .tm-alert__wrapper {
      flex: 0 1 auto;
    }
  }
}
</style>
